import React, { useState, useEffect, useCallback } from 'react';
import { therapistApi, scheduleApi } from '../services/api';
import { paymentsApi } from '../services/feesApi';
import { Therapist, Schedule } from '../types';
import { handleApiError, handleSuccess } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import InvoiceCreator from '../components/Fees/InvoiceCreator';
import './Reports.css';

interface InvoiceReportRow {
  id: number;
  receiptVoucherNumber: string;
  paymentDate: string;
  amountPaid: number;
  paymentMethod: string;
  studentName: string;
  feeDescription: string;
  gradeLevel: string;
  notes: string;
  source: 'payment' | 'invoice';
}

interface InvoiceSummary {
  totalPayments: number;
  totalAmount: number;
  startDate: string;
  endDate: string;
}

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState('scheduler');
  const [selectedTherapist, setSelectedTherapist] = useState('');
  const [startWeek, setStartWeek] = useState('');
  const [endWeek, setEndWeek] = useState('');
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Invoice report state
  const [invoiceStartDate, setInvoiceStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoiceEndDate, setInvoiceEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [invoicePayments, setInvoicePayments] = useState<InvoiceReportRow[]>([]);
  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummary | null>(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);

  const reportTypes = [
    { value: 'scheduler', label: 'Scheduler' },
    { value: 'invoices', label: 'Invoices' },
    { value: 'therapist-summary', label: 'Therapist Summary' },
    { value: 'student-summary', label: 'Student Summary' }
  ];

  useEffect(() => {
    loadTherapists();
  }, []);

  const loadTherapists = async () => {
    try {
      const response = await therapistApi.getAll({ active: true, limit: 100 });
      setTherapists(response.data.therapists);
    } catch (error) {
      handleApiError(error as any, 'Failed to load therapists');
    }
  };

  const loadSchedules = useCallback(async () => {
    if (!selectedTherapist || !startWeek || !endWeek) return;

    setLoading(true);
    try {
      const response = await scheduleApi.getAll({
        startDate: startWeek,
        endDate: endWeek
      });

      // Filter schedules by selected therapist
      const filteredSchedules = response.data.schedules.filter(
        schedule => schedule.therapistId === parseInt(selectedTherapist)
      );

      setSchedules(filteredSchedules);
    } catch (error) {
      handleApiError(error as any, 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [selectedTherapist, startWeek, endWeek]);

  useEffect(() => {
    if (selectedTherapist && startWeek && endWeek) {
      loadSchedules();
    }
  }, [selectedTherapist, startWeek, endWeek, loadSchedules]);

  const handleExportPDF = async () => {
    if (!selectedTherapist || !startWeek || !endWeek) {
      handleApiError({ error: 'Please select therapist and date range' } as any);
      return;
    }

    setGenerating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      handleSuccess('Report exported successfully');
    } catch (error) {
      handleApiError(error as any, 'Failed to export report');
    } finally {
      setGenerating(false);
    }
  };

  // Invoice report functions
  const loadInvoiceReport = async () => {
    if (!invoiceStartDate || !invoiceEndDate) {
      toast.error('Please select start and end dates');
      return;
    }

    setLoading(true);
    setInvoicePayments([]);
    setInvoiceSummary(null);
    try {
      const response = await paymentsApi.getInvoiceReport(invoiceStartDate, invoiceEndDate);
      setInvoicePayments(response.data.payments || []);
      setInvoiceSummary(response.data.summary || null);
    } catch (error: any) {
      console.error('Error loading invoice report:', error);
      toast.error('Failed to load invoice report');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (row: InvoiceReportRow) => {
    try {
      if (row.source === 'invoice') {
        await paymentsApi.downloadInvoiceById(row.id);
      } else {
        await paymentsApi.downloadReceipt(row.id, row.receiptVoucherNumber);
      }
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handlePrintInvoice = async (row: InvoiceReportRow) => {
    try {
      if (row.source === 'invoice') {
        await paymentsApi.printInvoiceById(row.id);
      } else {
        await paymentsApi.printReceiptById(row.id);
      }
    } catch (error: any) {
      console.error('Error printing invoice:', error);
      toast.error('Failed to open invoice for printing');
    }
  };

  const handleEditInvoice = (row: InvoiceReportRow) => {
    if (row.source !== 'invoice') {
      toast.info('Fee payment receipts cannot be edited from here');
      return;
    }
    setEditingInvoiceId(row.id);
  };

  const handleEditComplete = () => {
    setEditingInvoiceId(null);
    // Refresh the report data
    if (invoiceStartDate && invoiceEndDate) {
      loadInvoiceReport();
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleExportInvoicePDF = async () => {
    if (invoicePayments.length === 0) {
      toast.error('No data to export');
      return;
    }

    setGenerating(true);
    try {
      await paymentsApi.downloadInvoiceReportPDF({
        startDate: invoiceStartDate,
        endDate: invoiceEndDate,
        payments: invoicePayments,
        summary: invoiceSummary
      });
      toast.success('Invoice report PDF downloaded');
    } catch (error: any) {
      console.error('Error exporting invoice report:', error);
      toast.error('Failed to export invoice report');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderScheduleTable = () => {
    if (schedules.length === 0) {
      return (
        <div className="no-data">
          <p>No schedules found for the selected criteria.</p>
        </div>
      );
    }

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <div className="schedule-report-table">
        <h3>Weekly Schedule Report</h3>
        {schedules.map((schedule, index) => (
          <div key={index} className="schedule-week">
            <div className="schedule-header">
              <h4>
                Week: {new Date(schedule.weekStartDate).toLocaleDateString()} - {new Date(schedule.weekEndDate).toLocaleDateString()}
              </h4>
              <p>Therapist: {schedule.therapistName}</p>
            </div>

            <table className="report-table">
              <thead>
                <tr>
                  <th className="weekday-header">Weekday</th>
                  <th className="session-header">Period 1<br/>09:00:00 - 09:30:00</th>
                  <th className="session-header">Period 2<br/>09:30:00 - 10:00:00</th>
                  <th className="session-header">Period 3<br/>10:00:00 - 10:30:00</th>
                  <th className="session-header">Morning Break<br/>10:30:00 - 11:00:00</th>
                  <th className="session-header">Period 4<br/>11:00:00 - 11:30:00</th>
                  <th className="session-header">Period 5<br/>11:30:00 - 12:00:00</th>
                  <th className="session-header">Lunch Break<br/>12:00:00 - 13:00:00</th>
                </tr>
              </thead>
              <tbody>
                {days.map(day => (
                  <tr key={day}>
                    <td className="weekday-cell">{day}</td>
                    <td className="assignment-cell">Student Name</td>
                    <td className="assignment-cell">Student Name</td>
                    <td className="assignment-cell">Student Name</td>
                    <td className="break-cell">Break</td>
                    <td className="assignment-cell">Student Name</td>
                    <td className="assignment-cell">Student Name</td>
                    <td className="break-cell">Lunch</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  const renderInvoiceReport = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading invoice data...</p>
        </div>
      );
    }

    if (invoicePayments.length === 0) {
      return (
        <div className="no-data">
          <p>{invoiceSummary ? 'No invoices found for the selected date range.' : 'Select a date range and click Generate Report.'}</p>
        </div>
      );
    }

    const totalAmount = invoiceSummary?.totalAmount || invoicePayments.reduce((s, p) => s + parseFloat(String(p.amountPaid || 0)), 0);

    return (
      <div className="invoice-report-section">
        <h3>Invoices Summary</h3>
        <p className="invoice-report-subtitle">
          {formatDate(invoiceStartDate)} &mdash; {formatDate(invoiceEndDate)}
        </p>

        <div className="invoice-summary-cards">
          <div className="invoice-summary-card">
            <div className="invoice-card-value">{invoicePayments.length}</div>
            <div className="invoice-card-label">Total Invoices</div>
          </div>
          <div className="invoice-summary-card">
            <div className="invoice-card-value">AED {formatCurrency(totalAmount)}</div>
            <div className="invoice-card-label">Total Amount</div>
          </div>
        </div>

        <div className="invoice-report-table-wrapper">
          <table className="invoice-report-table">
            <thead>
              <tr>
                <th className="col-sno">#</th>
                <th className="col-receipt">Receipt / Invoice No.</th>
                <th className="col-date">Date</th>
                <th className="col-student">Student</th>
                <th className="col-desc">Description</th>
                <th className="col-method">Method</th>
                <th className="col-amount">Amount (AED)</th>
                <th className="col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {invoicePayments.map((p, idx) => (
                <tr key={`${p.source || 'payment'}-${p.id}`}>
                  <td className="col-sno">{idx + 1}</td>
                  <td>{p.receiptVoucherNumber}</td>
                  <td>{formatDate(p.paymentDate)}</td>
                  <td>{p.studentName}</td>
                  <td>{p.feeDescription}</td>
                  <td><span className={`method-badge method-${(p.paymentMethod || 'CASH').toLowerCase().replace(/\s+/g, '-')}`}>{p.paymentMethod || 'CASH'}</span></td>
                  <td className="col-amount">{formatCurrency(parseFloat(String(p.amountPaid)))}</td>
                  <td className="col-action">
                    <div className="action-buttons">
                      <button
                        className="action-btn action-btn-view"
                        onClick={() => handleViewInvoice(p)}
                        title="Download"
                      >
                        View
                      </button>
                      <button
                        className="action-btn action-btn-print"
                        onClick={() => handlePrintInvoice(p)}
                        title="Print"
                      >
                        Print
                      </button>
                      {p.source === 'invoice' && (
                        <button
                          className="action-btn action-btn-edit"
                          onClick={() => handleEditInvoice(p)}
                          title="Edit Invoice"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={7} className="total-label">Grand Total</td>
                <td className="col-amount total-amount">AED {formatCurrency(totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <h1>REPORTS</h1>
        <p>List of Reports</p>
      </div>

      <div className="reports-content">
        <div className="reports-sidebar">
          <div className="report-filters">
            <div className="filter-group">
              <label htmlFor="reportType">Report Title</label>
              <select
                id="reportType"
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  // Reset data when switching
                  setSchedules([]);
                  setInvoicePayments([]);
                  setInvoiceSummary(null);
                }}
                className="filter-select"
              >
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {reportType === 'scheduler' && (
              <>
                <div className="filter-group">
                  <label htmlFor="therapist">Therapist</label>
                  <select
                    id="therapist"
                    value={selectedTherapist}
                    onChange={(e) => setSelectedTherapist(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Select Therapist</option>
                    {therapists.map(therapist => (
                      <option key={therapist.id} value={therapist.id}>
                        {therapist.name} - {therapist.specialization}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="startWeek">Start Week</label>
                  <input
                    type="date"
                    id="startWeek"
                    value={startWeek}
                    onChange={(e) => setStartWeek(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="endWeek">End Week</label>
                  <input
                    type="date"
                    id="endWeek"
                    value={endWeek}
                    onChange={(e) => setEndWeek(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-actions">
                  <button
                    onClick={loadSchedules}
                    disabled={!selectedTherapist || !startWeek || !endWeek || loading}
                    className="filter-button"
                  >
                    {loading ? 'Loading...' : 'Generate Report'}
                  </button>

                  <button
                    onClick={handleExportPDF}
                    disabled={schedules.length === 0 || generating}
                    className="export-button"
                  >
                    {generating ? 'Exporting...' : 'Export PDF'}
                  </button>

                  <button
                    onClick={handlePrintReport}
                    disabled={schedules.length === 0}
                    className="print-button"
                  >
                    Print
                  </button>
                </div>
              </>
            )}

            {reportType === 'invoices' && (
              <>
                <div className="filter-group">
                  <label htmlFor="invoiceStartDate">Start Date</label>
                  <input
                    type="date"
                    id="invoiceStartDate"
                    value={invoiceStartDate}
                    onChange={(e) => setInvoiceStartDate(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="invoiceEndDate">End Date</label>
                  <input
                    type="date"
                    id="invoiceEndDate"
                    value={invoiceEndDate}
                    onChange={(e) => setInvoiceEndDate(e.target.value)}
                    className="filter-input"
                  />
                </div>

                <div className="filter-actions">
                  <button
                    onClick={loadInvoiceReport}
                    disabled={!invoiceStartDate || !invoiceEndDate || loading}
                    className="filter-button"
                  >
                    {loading ? 'Loading...' : 'Generate Report'}
                  </button>

                  <button
                    onClick={handleExportInvoicePDF}
                    disabled={invoicePayments.length === 0 || generating}
                    className="export-button"
                  >
                    {generating ? 'Exporting...' : 'Export PDF'}
                  </button>

                  <button
                    onClick={handlePrintReport}
                    disabled={invoicePayments.length === 0}
                    className="print-button"
                  >
                    Print
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="reports-main">
          {reportType === 'scheduler' && (
            <>
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading schedules...</p>
                </div>
              ) : (
                renderScheduleTable()
              )}
            </>
          )}

          {reportType === 'invoices' && renderInvoiceReport()}

          {reportType !== 'scheduler' && reportType !== 'invoices' && (
            <div className="coming-soon">
              <h3>{reportTypes.find(t => t.value === reportType)?.label} Report</h3>
              <p>This report type is coming soon.</p>
            </div>
          )}
        </div>
      </div>

      {editingInvoiceId && (
        <InvoiceCreator
          editInvoiceId={editingInvoiceId}
          onClose={() => setEditingInvoiceId(null)}
          onEditComplete={handleEditComplete}
        />
      )}
    </div>
  );
};

export default Reports;
